import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useUserLab } from './hooks/useUserLab';
import SignIn from './components/SignIn';
import LabGate from './components/LabGate';
import Layout from './components/Layout';
import ProjectList from './components/ProjectList';
import ProjectView from './components/ProjectView';
import ExperimentView from './components/ExperimentView';
import Legal from './components/Legal';
import DownloadPage from './components/Download';
import Billing from './components/Billing';
import OrgDashboard from './components/OrgDashboard';
import ProgressUpdates from './components/ProgressUpdates';
import Paid from './components/Paid';
import LabOnboarding from './components/LabOnboarding';
import { Spinner } from './components/ui';
import type { Lab } from './lib/lab';
import { clearStoredLab, getStoredLab } from './lib/lab';

/**
 * Privacy, terms and support sit outside every gate.
 * App stores require these to open cold, with no code and no account — a
 * reviewer following the link from a listing has neither.
 */
const PUBLIC_PATHS = ['/privacy', '/terms', '/support', '/download', '/paid'];

function PublicRoutes() {
  return (
    <Routes>
      <Route path="/privacy" element={<Legal />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/support" element={<Legal />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/paid" element={<Paid />} />
    </Routes>
  );
}

export default function App() {
  // Subscribing to location is what makes the public/app split re-evaluate on
  // navigation. Reading window.location directly does not re-render this
  // component, which left the app blank when returning from /download.
  const location = useLocation();
  const { user, loading } = useAuth();
  const { labCode, labName, orgRole, loading: labLoading } = useUserLab(user?.uid);
  const [lab, setLab] = useState<Lab | null>(() => getStoredLab());
  // Signing out clears the stored lab; re-read it so the sign-in screen does
  // not keep showing the previous account's lab from memory.
  useEffect(() => {
    if (!loading && !user) setLab(getStoredLab());
  }, [user, loading]);
  /**
   * The lab code screen, shown only when it is asked for.
   *
   * It used to stand in front of everything, which made the app unusable by
   * anyone who had not been sent a code — including an App Store reviewer, who
   * rejected it for exactly that (guideline 2.1, September 2026). Anyone can
   * sign in and keep their own records now; a code attaches the account to a
   * lab that already exists, and that is all it has ever really done. What
   * protects data is the Firestore rules, not this screen.
   */
  const [labPrompt, setLabPrompt] = useState(false);

  if (PUBLIC_PATHS.includes(location.pathname)) return <PublicRoutes />;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-7" />
      </div>
    );
  }

  if (labPrompt) {
    return (
      <LabGate
        onUnlocked={(joined) => {
          setLab(joined);
          setLabPrompt(false);
        }}
        onCancel={() => setLabPrompt(false)}
      />
    );
  }

  if (!user) {
    return (
      <SignIn
        lab={lab}
        onChangeLab={() => {
          clearStoredLab();
          setLab(null);
        }}
        onEnterLabCode={() => setLabPrompt(true)}
      />
    );
  }

  /**
   * Every account belongs to a lab, and the step that arranges it sits here —
   * after sign-in, before the app.
   *
   * Not before sign-in, which is what the rejected build did: a code screen in
   * front of everything left anyone without a code, reviewer included, with
   * nowhere to go. Here both answers are self-serve and take one step each.
   */
  if (labLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-7" />
      </div>
    );
  }

  if (!labCode) return <LabOnboarding onDone={setLab} />;

  return (
    <Layout
      lab={{
        code: labCode,
        name: labName,
        // Only used for display, and the account does not carry it.
        organisation: lab?.organisation ?? '',
        active: true,
      }}
      orgRole={orgRole}
    >
      <Routes>
        <Route path="/" element={<ProjectList />} />
        <Route path="/p/:projectId" element={<ProjectView />} />
        <Route path="/p/:projectId/e/:experimentId" element={<ExperimentView />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/org" element={<OrgDashboard />} />
        <Route path="/updates" element={<ProgressUpdates code={labCode} orgRole={orgRole} />} />
        <Route path="/updates/:folderId" element={<ProgressUpdates code={labCode} orgRole={orgRole} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
